import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { downloadBlob, toCsv } from "../Employees/employeeFileUtils";
import { exportRowsToXlsx } from "../reports/employeeReportsUtils";

const ADVANCE_CODES = new Set(["ADVANCE_TAKEN", "ADVANCE", "ADVANCE_RECOVERY", "SALARY_ADVANCE"]);
const OVERTIME_CODES = new Set(["OVERTIME", "OT"]);
const ENCASH_CODES = new Set(["LEAVE_ENCASH", "LEAVE_ENCASHMENT", "EL_ENCASHMENT"]);
const FUEL_CODES = new Set(["FUEL_REIMBURSEMENT", "FUEL", "FUEL_ALLOWANCE", "FUEL_ALLOW"]);

function round2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function codeOf(line) {
  return String(line?.component_code || "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function nameOf(line) {
  return String(line?.name || "").trim();
}

function classifyLine(line) {
  const code = codeOf(line);
  const name = nameOf(line).toLowerCase();
  if (ADVANCE_CODES.has(code) || name.includes("advance")) return "advance";
  if (OVERTIME_CODES.has(code) || name.includes("overtime")) return "overtime";
  if (ENCASH_CODES.has(code) || name.includes("encash")) return "encashment";
  if (FUEL_CODES.has(code) || name.includes("fuel")) return "fuel";
  return "other";
}

function componentLabel(line) {
  const name = nameOf(line);
  if (name) return name;
  const code = codeOf(line).replace(/_/g, " ").toLowerCase();
  return code ? code.replace(/\b\w/g, (ch) => ch.toUpperCase()) : "Other";
}

/**
 * One row per employee. Fixed payroll fields are always present.
 * Every other component on the run is added under its own clear name.
 */
export function buildPayrollDownload(employeeRows, statement) {
  const byEmployee = new Map(
    (statement || []).map((row) => [String(row.employee_id), row])
  );
  const extraLabels = [];
  const seenExtra = new Set();

  const records = (employeeRows || []).map((row) => {
    const lines = byEmployee.get(String(row.employee_id))?.component_lines || [];
    const buckets = { advance: 0, overtime: 0, encashment: 0, fuel: 0 };
    const extras = {};
    for (const line of lines) {
      const amount = round2(line?.amount);
      const bucket = classifyLine(line);
      if (bucket === "other") {
        const label = componentLabel(line);
        extras[label] = round2((extras[label] || 0) + amount);
        if (!seenExtra.has(label)) {
          seenExtra.add(label);
          extraLabels.push(label);
        }
      } else {
        buckets[bucket] = round2(buckets[bucket] + amount);
      }
    }
    return {
      "Employee name": row.employee_name || "",
      "Employee ID": row.employee_code || row.employee_id || "",
      "Gross salary": round2(row.earnings),
      Deductions: round2(row.deductions),
      "Net pay": round2(row.net_salary),
      "Advance taken": buckets.advance,
      "Overtime amount": buckets.overtime,
      "Leave encashment": buckets.encashment,
      "Fuel allowance": buckets.fuel,
      extras,
    };
  });

  extraLabels.sort((a, b) => a.localeCompare(b));
  const headers = [
    "Employee name",
    "Employee ID",
    "Gross salary",
    "Deductions",
    "Net pay",
    "Advance taken",
    "Overtime amount",
    "Leave encashment",
    "Fuel allowance",
    ...extraLabels,
  ];

  const flat = records.map((row) => {
    const out = {};
    headers.forEach((header) => {
      if (header in row) out[header] = row[header];
      else out[header] = round2(row.extras[header] || 0);
    });
    return out;
  });

  if (flat.length) {
    const total = { "Employee name": "Total", "Employee ID": "" };
    headers.slice(2).forEach((header) => {
      total[header] = round2(flat.reduce((sum, row) => sum + Number(row[header] || 0), 0));
    });
    flat.push(total);
  }

  return { headers, rows: flat };
}

function fileBase(periodLabel) {
  const slug = String(periodLabel || "payroll")
    .replace(/[^\w]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
  return slug || "payroll";
}

export function downloadPayrollFile(format, { employeeRows, statement, periodLabel, processedAt }) {
  const { headers, rows } = buildPayrollDownload(employeeRows, statement);
  const base = fileBase(periodLabel);
  if (format === "csv") {
    const csv = toCsv(rows, headers);
    downloadBlob(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }), `${base}.csv`);
    return;
  }
  if (format === "xlsx") {
    exportRowsToXlsx(rows, "Payroll", `${base}.xlsx`);
    return;
  }
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(14);
  doc.text("Processed payroll", 40, 36);
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  const sub = [
    periodLabel ? `Payroll ID: ${periodLabel}` : "",
    processedAt ? `Processed: ${processedAt}` : "",
    `${Math.max(0, rows.length - (rows.length ? 1 : 0))} employees`,
  ]
    .filter(Boolean)
    .join("   ·   ");
  doc.text(sub, 40, 52);
  autoTable(doc, {
    startY: 66,
    head: [headers],
    body: rows.map((row) => headers.map((header) => row[header])),
    styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: "bold" },
    foot: [],
    margin: { left: 28, right: 28 },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === rows.length - 1) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [241, 245, 249];
      }
    },
  });
  doc.save(`${base}.pdf`);
}
